import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Download, History, RefreshCw } from "lucide-react";
import {
  Alert,
  Button,
  CollapsibleSection,
  CopyField,
  DataTable,
  FeedbackState,
  FormDrawer,
  localTablePage,
  Mono,
  PropertyList,
  Skeleton,
  StatusBadge,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import { RELOAD_LOG_ERROR_CODES, type ReloadRun } from "@repo/shared";

import { toast } from "@/components/shared";
import { reloadAppRunsQueryOptions, reloadLogQueryOptions } from "@/features/reloads/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import { ApiError } from "@/lib/api-client";
import type { ReloadPeriodParams } from "@/lib/api/reloads";
import { formatSeconds } from "@/lib/duration-format";
import { paginationLabels, tableMessages } from "@/lib/ui-labels";
import { RunsChart } from "./charts";
import {
  appLabel,
  failureReason,
  reloadStatusLabel,
  reloadStatusTone,
  shortFailureReason,
  SITUATION_TONE,
  situationOf,
} from "./labels";
import { QlikAppLink } from "./QlikAppLink";
import { reloadLogSegments, saveReloadLog } from "./reload-log";

const PAGE_SIZE = 25;
const FAILED = new Set(["FAILED", "EXCEEDED_LIMIT"]);

const secondsBetween = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / 1000;

// The row action that opens an app's runs.
export function ViewRunsButton({
  app,
  onClick,
}: {
  app: { appId: string; appName: string | null };
  onClick: () => void;
}) {
  const { t } = useTranslation("reloads");
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={t("actions.viewRunsFor", { name: appLabel(app) })}
      title={t("actions.viewRuns")}
      onClick={onClick}
    >
      <History />
    </Button>
  );
}

// "Script executado": the reload's log, read from Qlik only once opened (the
// closed section is unmounted, and the query waits for `open`).
function ReloadLogSection({
  connectionId,
  appId,
  reloadId,
}: {
  connectionId: string;
  appId: string;
  reloadId: string;
}) {
  const { t } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const query = useQuery({
    ...reloadLogQueryOptions(connectionId, appId, reloadId),
    enabled: open,
  });
  const log = query.data;
  const segments = useMemo(() => (log === undefined ? [] : reloadLogSegments(log)), [log]);

  const body = () => {
    if (query.isPending) return <p className="text-sm text-dim">{t("runs.log.loading")}</p>;
    if (query.isError) {
      const code = query.error instanceof ApiError ? query.error.code : null;
      if (code === RELOAD_LOG_ERROR_CODES.unavailable)
        return <Alert tone="info">{t("runs.log.unavailable")}</Alert>;
      if (code === RELOAD_LOG_ERROR_CODES.tooLarge)
        return <Alert tone="info">{t("runs.log.tooLarge")}</Alert>;
      if (code === RELOAD_LOG_ERROR_CODES.forbidden)
        return (
          <Alert tone="warning" title={t("runs.log.forbidden.title")}>
            {t("runs.log.forbidden.description")}
          </Alert>
        );
      return (
        <Alert
          tone="danger"
          action={
            <Button variant="outline" size="compact" onClick={() => void query.refetch()}>
              <RefreshCw />
              {tc("tryAgain")}
            </Button>
          }
        >
          {t("runs.log.failed")}
        </Alert>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        <pre className="max-h-80 overflow-auto rounded-md bg-surface-muted p-3 font-mono text-[11.5px] leading-relaxed text-ink">
          {segments.map((segment, i) => (
            <span
              key={i}
              className={
                segment.error ? "bg-status-danger-bg text-status-danger-foreground" : undefined
              }
            >
              {i > 0 && "\n"}
              {segment.text}
            </span>
          ))}
        </pre>
        <p className="text-xs text-dim">{t("runs.log.note")}</p>
      </div>
    );
  };

  return (
    <CollapsibleSection
      variant="card"
      headingLevel={4}
      open={open}
      onOpenChange={setOpen}
      title={t("runs.log.title")}
      description={t("runs.log.description")}
      actions={
        log !== undefined && (
          <Button variant="outline" size="compact" onClick={() => saveReloadLog(log, reloadId)}>
            <Download />
            {t("runs.log.download")}
          </Button>
        )
      }
    >
      {body()}
    </CollapsibleSection>
  );
}

// An app's runs in the page's period and origin: its numbers, a column per
// run and the list, where a finished run opens to its failure reason and log.
// Mounted per app (the route keys it), so its table state starts fresh.
export function AppRunsDrawer({
  connectionId,
  baseUrl,
  appId,
  period,
  version,
  description,
  onClose,
}: {
  connectionId: string;
  baseUrl: string;
  appId: string;
  period: ReloadPeriodParams;
  version: string;
  description: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const { language } = useLocale();
  const { formatDate, formatDateTime } = useFormatDate();
  const query = useQuery(reloadAppRunsQueryOptions(connectionId, appId, period, version));
  const data = query.data;

  const [state, setState] = useState<DataTableState>({
    page: 1,
    pageSize: PAGE_SIZE,
    query: "",
    filters: {},
    selection: [],
  });
  // Until someone toggles a row, the newest failure starts open.
  const [expanded, setExpanded] = useState<string[] | null>(null);
  const newestFailure = data?.runs.find((run) => FAILED.has(run.status))?.reloadId;
  const openRows = expanded ?? (newestFailure ? [newestFailure] : []);

  const count = (value: number) => value.toLocaleString(language);
  const duration = (seconds: number | null) => (seconds === null ? "—" : formatSeconds(seconds));

  // The columns' widths add up to the table's minimum, which has to fit the
  // drawer: past it the open row (reason and log) would scroll sideways too.
  // The origin is the drawer's, in its description.
  const columns: DataTableColumn<ReloadRun>[] = [
    {
      id: "status",
      header: t("runs.list.columns.status"),
      width: "8.5rem",
      cell: (run) => (
        <StatusBadge tone={reloadStatusTone(run.status)}>
          {reloadStatusLabel(t, run.status)}
        </StatusBadge>
      ),
    },
    {
      id: "start",
      header: t("runs.list.columns.start"),
      width: "10.5rem",
      variant: "numeric",
      // To the minute; the seconds are in the tooltip.
      cell: (run) => {
        const at = run.startTime ?? run.creationTime;
        return (
          <span
            title={formatDateTime(at)}
          >{`${formatDate(at)} ${format(new Date(at), "HH:mm")}`}</span>
        );
      },
    },
    {
      id: "wait",
      header: t("runs.list.columns.wait"),
      width: "4.5rem",
      align: "end",
      variant: "numeric",
      cell: (run) =>
        run.startTime ? duration(secondsBetween(run.creationTime, run.startTime)) : "—",
    },
    {
      id: "duration",
      header: t("runs.list.columns.duration"),
      width: "6rem",
      align: "end",
      variant: "numeric",
      cell: (run) =>
        run.startTime && run.endTime ? duration(secondsBetween(run.startTime, run.endTime)) : "—",
    },
    {
      id: "reason",
      header: t("runs.list.columns.reason"),
      cell: (run) => {
        const reason = shortFailureReason(run.errorCode, run.errorMessage);
        return reason ? (
          <span
            className="text-status-danger-foreground"
            title={failureReason(run.errorCode, run.errorMessage)}
          >
            {reason}
          </span>
        ) : (
          ""
        );
      },
    },
  ];

  const runsPage = localTablePage(data?.runs ?? [], state, { columns });

  const title = data ? (
    <span className="inline-flex max-w-full items-center gap-2">
      <QlikAppLink app={data.app} baseUrl={baseUrl} />
      {data.summary.failed > 0 && (
        <StatusBadge tone={SITUATION_TONE[situationOf(data.summary)]}>
          {t(`errors.failingApps.situation.${situationOf(data.summary)}`)}
        </StatusBadge>
      )}
    </span>
  ) : (
    appId
  );

  const content = () => {
    if (query.isPending) return <Skeleton className="h-64 w-full" />;
    if (query.isError || !data) {
      return (
        <FeedbackState
          state="error"
          title={t("runs.loadFailed")}
          action={
            <Button variant="outline" onClick={() => void query.refetch()}>
              <RefreshCw />
              {tc("tryAgain")}
            </Button>
          }
        />
      );
    }
    const s = data.summary;
    return (
      <div className="flex flex-col gap-5">
        <PropertyList
          columns={2}
          items={[
            {
              id: "failures",
              label: t("runs.summary.failures"),
              value: t("errors.failingApps.failedOf", {
                failed: count(s.failed),
                total: count(s.total),
              }),
              mono: true,
            },
            {
              id: "streak",
              label: t("runs.summary.streak"),
              value: count(s.currentStreak),
              mono: true,
            },
            {
              id: "lastSuccess",
              label: t("runs.summary.lastSuccess"),
              value: s.lastSuccessAt ? formatDateTime(s.lastSuccessAt) : "—",
              mono: true,
            },
            {
              id: "median",
              label: t("runs.summary.median"),
              value: duration(s.medianSeconds),
              mono: true,
            },
            { id: "p90", label: "p90", value: duration(s.p90Seconds), mono: true },
            {
              id: "wait",
              label: t("runs.summary.wait"),
              value: duration(s.medianWaitSeconds),
              mono: true,
            },
            {
              id: "appId",
              label: t("runs.summary.appId"),
              full: true,
              value: (
                <CopyField
                  value={appId}
                  copyLabel={t("runs.summary.copyAppId")}
                  copiedLabel={t("runs.summary.copied")}
                  onCopyError={() => toast.error(t("errors.groups.detail.copyFailed"))}
                />
              ),
            },
          ]}
        />

        <RunsChart runs={data.runs} medianSeconds={s.medianSeconds} p90Seconds={s.p90Seconds} />

        <section className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h3 className="text-sm font-semibold text-ink">{t("runs.list.title")}</h3>
            <span className="text-xs text-dim">
              {data.truncated
                ? t("runs.list.truncated", { count: data.runs.length })
                : t("runs.list.note")}
            </span>
          </div>
          <DataTable
            label={t("runs.list.title")}
            columns={columns}
            rows={runsPage.rows}
            getRowId={(run) => run.reloadId}
            state={state}
            onStateChange={setState}
            expansion={{
              expanded: openRows,
              onExpandedChange: setExpanded,
              canExpand: (run) => run.endTime !== null,
              getRowLabel: (run) => formatDateTime(run.startTime ?? run.creationTime),
              labels: {
                column: t("runs.list.expandColumn"),
                expand: (label) => t("runs.list.expand", { label }),
                collapse: (label) => t("runs.list.collapse", { label }),
              },
              render: (run) => (
                <div className="flex flex-col gap-3 py-2">
                  {FAILED.has(run.status) && (
                    <Alert tone="danger" title={t("runs.detail.reason")}>
                      <Mono className="whitespace-pre-wrap">
                        {failureReason(run.errorCode, run.errorMessage) ||
                          t("runs.detail.noReason")}
                      </Mono>
                    </Alert>
                  )}
                  <ReloadLogSection
                    connectionId={connectionId}
                    appId={appId}
                    reloadId={run.reloadId}
                  />
                </div>
              ),
            }}
            pagination={
              runsPage.total > PAGE_SIZE
                ? { kind: "offset", total: runsPage.total, labels: paginationLabels() }
                : undefined
            }
            messages={tableMessages({ empty: { title: t("runs.list.empty") } })}
          />
        </section>
      </div>
    );
  };

  return (
    <FormDrawer
      open
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={title}
      description={description}
      closeLabel={tc("close")}
    >
      {content()}
    </FormDrawer>
  );
}
